import { Action } from '../control-flow/action-on-match';
import * as dm from '../domain';
import DomainModel from '../domain-model';
import logging from '../logging';
import { MaterialOptions } from '../views/views-config';
import { DefaultsConfig } from './defaults-config-options';
import { reset } from './defaults-manager-reset';

const log = logging('DefaultsManager');

const makeMaterial = (data: unknown) => dm.Material.fromJSON(data);

export class DefaultsManager {
    config: DefaultsConfig;

    private resetOne: Action<DomainModel>;

    constructor(config: DefaultsConfig) {
        this.config = config;
        this.resetOne = reset(config);
    }

    get(ctor: typeof DomainModel, props: Record<string, any>): DomainModel
    {
        const materials = this.config.materials;
        const styles = this.config.styles;
        switch(ctor) {
            case dm.ClosetUnit:
                break;

            case dm.Cubby:
                return dm.Cubby.fromJSON({
                    faceMaterial: materials.edgeband,
                    boxMaterial: materials.shelving,
                    ...props,
                });

            case dm.Door:
                return dm.Door.fromJSON({
                    frame: styles.doorFrame
                        ? {
                            style: styles.doorFrame,
                            material: materials.door,
                        }
                        : null,
                    panel: {
                        style: styles.doorPanel,
                        grain: "vertical",
                        material: materials.door,
                    },
                    handles:
                        props.hinge == 'double'
                        ? [
                            {
                                style: templ(styles.hardware, 'v'),
                                materials: [materials.hardware],
                            },
                            {
                                style: templ(styles.hardware, 'v'),
                                materials: [materials.hardware],
                            },
                          ]
                        : [
                            {
                                style: templ(
                                    styles.hardware,
                                    props.hinge == 'left' || props.hinge == 'right'
                                    ? 'v' : 'h'),
                                materials: [materials.hardware]
                            },
                          ],
                    ...props,
                });

            case dm.HookRack:
                break;

            case dm.VerticalPanel:
                return dm.VerticalPanel.fromJSON({
                    materials: [materials.shelving],
                    ...props,
                });

            case dm.Drawer:
                return dm.Drawer.fromJSON({
                    frame: props.scoop || styles.drawerFrame == null ? null : {
                        style: styles.drawerFrame,
                        material: materials.drawer,
                    },
                    panel: props.scoop
                        ? {
                            style: "",
                            grain: "horizontal",
                            material: materials.drawerBox
                          }
                        : {
                            style: props.height > 6 ? styles.drawerPanel : styles.drawerSlabPanel,
                            grain: "horizontal",
                            material: materials.drawer
                           },
                    handles: props.scoop
                        ? []
                        : [{
                            position: { x: 9, y: props.height/2 - 0.25, z: 0},
                            style: templ(styles.hardware, 'h'),
                            materials: [materials.hardware],
                          }],
                    box: this.get(dm.DrawerBox, {}),
                    ...props,
                });

            case dm.DrawerBox:
                return dm.DrawerBox.fromJSON({
                    materials: [materials.drawerBox],
                    ...props,
                });

            case dm.GenericAsset: {
                let mats: object;
                if(props.filename && props.filename.indexOf("TAG") != -1) {
                    const
                        [_tag, name, ..._extra] = props.filename.split("_"),
                        black = { type: "GlossBlack", slot: "black" };

                    switch(name) {
                        case "LaundryOrganizer":
                            mats = [
                                {type: "GlossBlack"},
                                materials.tagFrame,
                                materials.tagCanvas];
                            break;
                        case "PantRack":
                        case "ShoeOrganizer":
                            mats = [materials.tagFrame, black];
                            break;
                        default:
                            mats = [materials.tagFrame, materials.tagCanvas]
                    }
                }
                else if(props.assetId && props.assetId.includes('tag-curate')) {
                    // Eliminate the size variants
                    const
                        strippedId = props.assetId.slice(0, -6),
                        black = { type: "GlossBlack", slot: "black" };
                    switch(strippedId) {
                        case "parts/tag-curate-jewelry":
                        case "parts/tag-curate-shelf-drawer":
                        case "parts/tag-curate-tie":
                        case "parts/tag-curate-sunglasses":
                            mats = [materials.tagFrame, materials.tagLeatherInsert];
                            break;
                        case "parts/tag-curate-pants":
                            mats = [materials.tagFrame, black];
                            break;

                        case "parts/tag-curate-small-basket":
                        case "parts/tag-curate-medium-basket":
                        case "parts/tag-curate-large-basket":
                            mats = [
                                materials.tagFrame,
                                materials.tagFabricInsert,
                            ];
                            break;
                        case "parts/tag-curate-laundry":
                            mats = [
                                materials.tagFrame,
                                materials.tagFabricInsert,
                                black,
                            ];
                            break;
                        case "parts/tag-curate-shoe":
                            mats = [
                                materials.tagFrame,
                                materials.tagMeshInsert,
                                black,
                            ]
                    }
                }
                else {
                    mats = [];
                }
                return dm.GenericAsset.fromJSON({materials: mats, ...props});
            }

            // accepts a 'direction' property, either 'h' or 'v' to fill in the
            // handle filename template
            case dm.Handle:
                let hinge = '';
                if(props.hinge == 'right') hinge = 'r';

                return dm.Handle.fromJSON({
                    materials: [materials.hardware],
                    style: templ(styles.hardware, String(props.direction), hinge),
                    ...props,
                });

            case dm.ParametricPart: {
                let mats: MaterialOptions[];
                switch(props.name) {
                    case 'HangBar':
                        mats = [materials.rod];
                        break;
                    case 'GlassShelf':
                        mats = [{type: 'Glass'}];
                        break;
                    case 'AdjustableShelf':
                        mats = [ materials.shelving, materials.edgeband ];
                        break;
                    case 'FlatShoeShelf':
                        mats = [ materials.shelving, materials.edgeband ];
                        break;
                    case 'FixedShelf':
                        mats = [ materials.shelving, materials.edgeband ];
                        break;
                    case 'FixedShoeShelf':
                        mats = [ materials.shelving, materials.edgeband ];
                        break;
                    case 'SlidingShelf':
                        mats = [
                            materials.shelving,
                            materials.edgeband,
                            {type: 'Chrome'},
                        ];
                        break;
                    case 'SlantedShelfWithFence':
                        mats = [
                            materials.shelving,
                            materials.edgeband,
                            materials.hardware,
                        ];
                        break;
                    case 'FixedShelfWithPuckLight':
                        mats = [
                            materials.shelving,
                            materials.edgeband,
                            materials.hardware,
                            materials.light
                        ];
                        break;
                    case 'FixedShelfWithStripLight':
                        mats = [
                            materials.shelving,
                            materials.edgeband,
                            materials.light
                        ];
                        break;
                    default: 
                        mats = [materials.shelving];
                }
                return dm.ParametricPart.fromJSON({
                    materials: mats,
                    ...props,
                });
            }

            case dm.Wall:
                break;

            default:
                log.error('DefaultsManager get(): no default material for model');
        }
    }

    reset = (model: DomainModel) => {
        model.traverse(this.resetOne);
    };
}
export default DefaultsManager;

// Probably a lot more concise ways of writing this, but I think this is the
// clearest I can come up with. All # characters will be consumed unless they
// are escaped with a leading backslash, i.e. \#. Each additional argument is
// substituted for a # in the input until arguments run out, then the # is
// merely deleted an no error is logged.
const templ = (template: string, ...args: string[]): string => {
    let result = "";
    let i = 0;
    const segments = template.split(/(?<!\\)#/g);
    if(segments.length == 1) {
        return template;
    }
    for(const segment of segments) {
        result += segment;
        if(i < args.length) {
            result += args[i];
            i += 1;
        }
    }
    return result;
}



//        const materials = this.config.materials;
//        const styles = this.config.styles;
//
//        const ctor = Object.getPrototypeOf(model).constructor;
//        switch(ctor) {
//            case dm.ClosetUnit: {
//                //const unit = model as dm.ClosetUnit;
//                // nothing to do currently
//                break;
//            }
//            case dm.Cubby: {
//                const cubby = model as dm.Cubby;
//                cubby.faceMaterial = makeMaterial(materials.edgeband);
//                cubby.boxMaterial = makeMaterial(materials.shelving);
//                break;
//            }
//            case dm.Door: {
//                const door = model as dm.Door;
//                if(styles.doorFrame == null) {
//                    door.frame = null;
//                } else {
//                    door.frame = dm.Frame.fromJSON({
//                        style: styles.doorFrame,
//                        material: materials.door,
//                    }, door);
//                }
//
//                door.panel.style = styles.doorPanel;
//                door.panel.material = makeMaterial(materials.door);
//
//                let handle: dm.Handle;
//                if(door.handles.length > 0) {
//                    handle = dm.Handle.fromJSON(door.handles[0], door);
//                } else {
//                    handle = dm.Handle.fromJSON({}, door);
//                }
//                handle.style = templ(
//                        styles.hardware,
//                        door.hinge == 'left' || door.hinge == 'right'
//                        ? 'v' : 'h');
//                handle.materials = [makeMaterial(materials.hardware)];
//                if(door.hinge == 'double') {
//                    const left = handle;
//                    const right = handle.clone(true);
//                    door.handles = [left, right];
//                } else {
//                    door.handles = [handle];
//                }
//
//                break;
//            }
//            case dm.Drawer: {
//                const drawer = model as dm.Drawer;
//                if(styles.drawerFrame == null) {
//                    drawer.frame = null;
//                } else {
//                    drawer.frame = dm.Frame.fromJSON({
//                        style: styles.drawerFrame,
//                        material: materials.drawer,
//                    }, drawer);
//                }
//
//                drawer.panel.style = styles.drawerPanel;
//                drawer.panel.material = makeMaterial(materials.drawer);
//
//                let handle: dm.Handle;
//                if(drawer.handles.length > 0) {
//                    // we assume that normally the handle.side property is null
//                    // to imply the default position opposite the hinge. The
//                    // one likely other case would be if a cabinet has a full
//                    // width pull at the top or bottom, in which case they
//                    // both have the same setting.
//                    handle = dm.Handle.fromJSON(drawer.handles[0], drawer);
//                } else {
//                    handle = dm.Handle.fromJSON({}, drawer);
//                }
//                handle.style = templ(styles.hardware, 'h');
//                handle.materials = [makeMaterial(materials.hardware)];
//                drawer.handles = [handle];
//
//                break;
//            }
//            case dm.DrawerBox: {
//                const box = model as dm.DrawerBox;
//                box.materials = [makeMaterial(materials.drawerBox)];
//                break;
//            }
//            case dm.GenericAsset: {
//                const asset = model as dm.GenericAsset;
//                if(asset.filename.indexOf("TAG") == -1) {
//                    asset.materials = [];
//                } else {
//                    const [_tag, name, ..._extra] = asset.filename.split("_");
//                    switch(name) {
//                        case "LaundryOrganizer":
//                            asset.materials = [
//                                {type: "GlossBlack"},
//                                materials.tagFrame,
//                                materials.tagCanvas
//                        ].map(makeMaterial);
//                            break;
//                        case "PantRack":
//                        case "ShoeOrganizer":
//                            asset.materials = [
//                                materials.tagFrame,
//                                dm.Material.fromJSON({type: "GlossBlack"})
//                            ].map(makeMaterial);
//                            break;
//                        default:
//                            asset.materials = [materials.tagFrame, materials.tagCanvas]
//                                .map(makeMaterial);
//                    }
//                }
//                break;
//            }
//            case dm.ParametricPart: {
//                const part = model as dm.ParametricPart;
//                switch(part.name) {
//                    case 'HangBar':
//                        part.materials = [materials.rod].map(makeMaterial);
//                        break;
//                    case 'GlassShelf':
//                        part.materials = [dm.Material.fromJSON({type: 'Glass'})].map(makeMaterial);
//                        break;
//                    case 'FixedShoeShelf':
//                    case 'FlatShoeShelf':
//                    case 'AdjustableShelf':
//                        part.materials = [materials.shelving, materials.edgeband].map(makeMaterial);
//                        break;
//                    case 'SlidingShelf':
//                        part.materials = [
//                            materials.shelving,
//                            materials.edgeband,
//                            dm.Material.fromJSON({type: 'Chrome'})
//                        ].map(makeMaterial);
//                        break;
//                    case 'SlantedShelfWithFence':
//                        part.materials = [
//                            materials.shelving,
//                            materials.edgeband,
//                            materials.hardware,
//                        ].map(makeMaterial);
//                        break;
//
//                    default:
//                        part.materials = [materials.shelving].map(makeMaterial);
//                }
//            }
//            // There is no default case because this is expected to be called
//            // by the DomainModel.traverse function, in which case we will
//            // visit some items we don't need to modify at all.
//        }
